import {
  Divider,
  Grid,
  Typography,
  Snackbar,
  Alert as MuiAlert,
  CardContent,
  Box,
  Skeleton,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  styled,
  Pagination,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  Card,
  TextField
} from '@mui/material'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck } from '../../utils/utils'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  ShowNoRecordsFound,
  ErrorScreen,
  EnhancedAccordion,
  EnhancedAccordionSummary,
  GridRow,
  StyledTableCell,
  StyledTableRow
} from '../../utils/ui_utils'
import moment from 'moment'
import { BarChart } from '../../components/Chart'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'
import { OutletAndDatePickerUI } from '../../utils/ui_components/outletAndDatePickerUi'
import { useFetchOutletSummaryForFranchise } from '../../modules/reports/reportsHooks'
import { ArrowForwardIosSharp, ExpandMore, Store } from '@mui/icons-material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import { useDownloadReports } from '../../modules/reports/reportsHooks.ts'
import { generateXLSXCSV } from '../../utils/tsUtils/utils.js'

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const FranchisewiseSummaryReport = () => {
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [toDate, setToDate] = useState(moment().startOf('day'))
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showToDateError, setShowToDateError] = useState(false)
  const [toDateErrorMessage, setToDateErrorMessage] = useState(
    'To Date is earlier than From Date'
  )
  const [reportVisibility, setReportVisibility] = useState(false)
  const [chartVisibility, setChartVisibility] = useState(false)
  const {
    data: outletSummaryData,
    error: outletSummaryError,
    fetch: outletSummaryFetch,
    loading: outletSummaryLoading
  } = useFetchOutletSummaryForFranchise()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [pagecount, setPageCount] = useState(0)

  const [dailySalesChartData, setdailySalesChartData] = useState({
    labels: [moment().format('MMM-DD')],
    datasets: [
      {
        label: 'Total Sales',
        data: [0],
        backgroundColor: ['#ffbb11']
      }
    ]
  })

  const { downloadReportsLoading, downloadReportsFetch } = useDownloadReports()

  const toastMessage = (message, type = 'success') => {
    setPopUpMessage(message)
    setSeverity(type)
    setSnackBar(true)
  }

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const refreshFunction = () => validateAndGetReport(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } =
    useReportRefresh(refreshFunction, [page, fromDate, toDate])

  const validateAndGetReport = (page) => {
    let tempFromDate = moment(fromDate)
    let tempToDate = moment(toDate)
    if (tempFromDate.isAfter(tempToDate)) {
      return
    }
    fetchRecord()
  }

  const fetchRecord = (pageNo = page) => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: pageNo,
      limit: limit,
      by_business_name: true
    }
    outletSummaryFetch(payload)
  }

  const chartOptions = {
    responsive: true,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Outlets',
          align: 'start',
          font: {
            weight: 'bold',
            style: 'italic'
          }
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: 'Sales Trend',
        padding: {
          top: 10,
          bottom: 30
        }
      },
      legend: {
        display: true,
        position: 'bottom'
      },
      datalabels: {
        display: true,
        color: 'black',
        formatter: Math.round,
        anchor: 'end',
        offset: -20,
        align: 'start'
      }
    }
  }

  const history = useNavigate()

  const exportToCsv = async () => {
    try {
      let payload = {
        from_date: moment(fromDate).format('YYYY-MM-DD'),
        to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
        pageNumber: 1,
        limit: 100,
        total: outletSummaryData?.total || 15,
        type: 'franchisewise_summary',
        by_business_name: true
      }

      const reports = await downloadReportsFetch(payload)

      let data1 = [...reports.payload]

      const options = {
        keys: [
          { field: 'Sub_Business_Name', title: 'Business Name' },
          { field: 'Total_Bills', title: 'Total Bills' },
          { field: 'Total_Sales', title: 'Total Sales' },
          { field: 'Total_GST', title: 'GST' },
          { field: 'Total_Cess', title: 'Cess' },
          { field: 'Total_Disc', title: 'Discount' }
        ]
      }

      let filename = `Franchisewise_Summary_Report`
      filename += `_${payload.from_date}_to_${payload.to_date}`

      const csv = await generateXLSXCSV(data1, options.keys, filename)

      toastMessage('CSV Downloaded Successfully')
    } catch (error) {
      console.error('Error while converting JSON to CSV', error)
      toastMessage('Error While Downloading csv', 'error')
    }
  }

  useEffect(() => {
    businessCheck(history)
  }, [])

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }
  useEffect(() => {
    if (outletSummaryData?.data?.length > 0) {
      pageCount(outletSummaryData?.total)
      setdailySalesChartData({
        labels: outletSummaryData?.data?.map(
          (data) => data['Sub_Business_Name']
        ),
        datasets: [
          {
            label: 'Total Sales',
            data: outletSummaryData?.data?.map((data) => {
              return data['Total_Sales']
            }),
            backgroundColor: ['#e0218a', '#ffbb11', '#ff5e57', '#00d25b'],
            barThickness: 30
          }
        ]
      })
      setReportVisibility(true)
      setChartVisibility(true)
    } else {
      setdailySalesChartData([])
      setReportVisibility(false)
      setChartVisibility(false)
    }
  }, [outletSummaryData])

  const fetchSummaryOnError = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: page,
      limit: limit,
      by_business_name: true
    }
    outletSummaryFetch(payload)
  }

  useEffect(() => {
    validateAndGetReport(1)
  }, [fromDate, toDate])

  const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={10} {...props} />
  ))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:before': {
      display: 'none'
    }
  }))

  const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
      {...props}
    />
  ))(({ theme }) => ({
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, .05)'
        : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1)
    },
    root: {
      '& .MuiAccordionSummary:nth-of-type(even)': {
        backgroundColor: 'rgba(0, 0, 0, 0)'
      }
    }
  }))

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)'
  }))

  const [expanded, setExpanded] = useState(false)
  const handleExpandChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    fetchRecord(value)
  }

  const [showFullDetails, setShowFullDetails] = useState(false)
  const [goToPageInput, setGoToPageInput] = useState('')

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    // Allow empty or valid numbers
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  return (
    <>
      <Helmet title="Franchisewise Summary Report" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Franchisewise Summary Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={outletSummaryLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: 2 }} />
      {
        <div>
          {
            <OutletAndDatePickerUI
              onlyDateSelection={true}
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={(newValue) => {
                newValue.setHours(0, 0, 0)
                setFromDate(newValue)
                if (moment(toDate).isBefore(newValue)) {
                  setToDateErrorMessage('To Date is earler than From Date')
                  setShowToDateError(true)
                } else {
                  setToDateErrorMessage('')
                  setShowToDateError(false)
                }
              }}
              onFromDateError={(reason, value) => {
                if (reason) {
                  setErrorMessage(reason)
                  setShowError(true)
                } else {
                  setErrorMessage('')
                  setShowError(false)
                }
              }}
              onToDateChange={(newValue) => {
                newValue.setHours(0, 0, 0)
                setToDate(newValue)
                if (moment(fromDate).isAfter(newValue)) {
                  setToDateErrorMessage('To Date is earler than From Dates')
                  setShowToDateError(true)
                } else {
                  setToDateErrorMessage('')
                  setShowToDateError(false)
                }
              }}
              onToDateError={(reason, value) => {
                if (reason) {
                  setToDateErrorMessage(reason)
                  setShowToDateError(true)
                } else {
                  setToDateErrorMessage('')
                  setShowToDateError(false)
                }
              }}
              showFromDateError={showError}
              showToDateError={showToDateError}
              fromDateErrorMessage={errorMessage}
              toDateErrorMessage={toDateErrorMessage}
              exportComponent={
                outletSummaryData?.data &&
                outletSummaryData.data.length > 0 && (
                  <Tooltip title="Export to CSV" placement="top" arrow>
                    <IconButton
                      variant="contained"
                      onClick={exportToCsv}
                      disabled={downloadReportsLoading}
                      size="large"
                    >
                      {downloadReportsLoading && <CircularProgress size={20} />}
                      {!downloadReportsLoading ? (
                        <>
                          {' '}
                          <CloudUploadIcon fontSize="large" />
                        </>
                      ) : (
                        <> </>
                      )}
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          }
          {outletSummaryError ? (
            <ErrorScreen
              onClick={() => {
                fetchSummaryOnError()
              }}
            />
          ) : (
            <div>
              {(outletSummaryLoading == true ||
                outletSummaryLoading == null) && (
                <Grid container spacing={10} direction={'column'}>
                  <Grid item>
                    <Grid
                      container
                      rowSpacing={4.5}
                      columnSpacing={10}
                      sx={{ mt: 1 }}
                      justifyContent={'center'}
                    >
                      <Grid item xs={3} sm={3} md={8}>
                        <Grid container justifyContent={'center'}>
                          <Skeleton
                            variant="rectangular"
                            animation="wave"
                            width={120}
                            height={36}
                            sx={{ borderRadius: 1 }}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={3} sm={3} md={8}>
                        <MainCard
                          content={false}
                          sx={{
                            mt: 1.5,
                            width: '100%',
                            maxWidth: 'calc(100vw - 300px)',
                            '@media (max-width: 960px)': {
                              maxWidth: '100vw'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              pt: 1,
                              pr: 2,
                              pl: 2,
                              pb: 1,
                              height: '400px',
                              overflow: 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            {/* Bar chart skeleton */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                gap: 2,
                                height: '80%',
                                width: '80%'
                              }}
                            >
                              <Skeleton
                                variant="rectangular"
                                animation="wave"
                                width={60}
                                height="60%"
                                sx={{ borderRadius: '4px 4px 0 0' }}
                              />
                              <Skeleton
                                variant="rectangular"
                                animation="wave"
                                width={60}
                                height="80%"
                                sx={{ borderRadius: '4px 4px 0 0' }}
                              />
                              <Skeleton
                                variant="rectangular"
                                animation="wave"
                                width={60}
                                height="45%"
                                sx={{ borderRadius: '4px 4px 0 0' }}
                              />
                              <Skeleton
                                variant="rectangular"
                                animation="wave"
                                width={60}
                                height="70%"
                                sx={{ borderRadius: '4px 4px 0 0' }}
                              />
                            </Box>
                          </Box>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {!outletSummaryLoading &&
                !outletSummaryError &&
                reportVisibility && (
                  <Grid container spacing={10} direction={'column'}>
                    {chartVisibility && (
                      <Grid item>
                        <Grid
                          container
                          rowSpacing={4.5}
                          columnSpacing={10}
                          sx={{ mt: 1 }}
                          justifyContent={'center'}
                        >
                          <Grid item xs={3} sm={3} md={8}>
                            <Grid container justifyContent={'center'}>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() =>
                                  setShowFullDetails(!showFullDetails)
                                }
                              >
                                {showFullDetails
                                  ? 'Hide Details'
                                  : 'Show Details'}
                              </Button>
                            </Grid>
                          </Grid>
                          <Grid item xs={3} sm={3} md={8}>
                            {!showFullDetails && (
                              <Typography variant="h5" gutterBottom>
                                Sales Trend
                              </Typography>
                            )}
                            <MainCard
                              content={false}
                              sx={{
                                mt: 1.5,
                                width: '100%',
                                maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
                                '@media (max-width: 960px)': {
                                  maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
                                }
                              }}
                            >
                              {!showFullDetails ? (
                                <Box
                                  sx={{
                                    pt: 1,
                                    pr: 2,
                                    pl: 2,
                                    pb: 1,
                                    height: '400px', // Responsive height
                                    overflow: 'auto'
                                  }}
                                >
                                  <BarChart
                                    chartData={dailySalesChartData}
                                    options={chartOptions}
                                  />
                                </Box>
                              ) : (
                                <CardContent sx={{ flexGrow: 1 }}>
                                  <TableContainer
                                    component={Card}
                                    sx={{
                                      width: '100%',
                                      maxWidth: '100%',
                                      overflow: 'auto'
                                    }}
                                  >
                                    <Table size="small">
                                      <TableHead size="small">
                                        <StyledTableRow>
                                          <StyledTableCell>
                                            Business Name
                                          </StyledTableCell>
                                          <StyledTableCell align="right">
                                            Total Bills
                                          </StyledTableCell>
                                          <StyledTableCell align="right">
                                            Total Sales
                                          </StyledTableCell>
                                          <StyledTableCell align="right">
                                            GST
                                          </StyledTableCell>
                                          <StyledTableCell align="right">
                                            Cess
                                          </StyledTableCell>
                                          <StyledTableCell align="right">
                                            Discount
                                          </StyledTableCell>
                                        </StyledTableRow>
                                      </TableHead>
                                      <TableBody>
                                        {outletSummaryData?.data?.map(
                                          (row, i) => (
                                            <StyledTableRow key={i} hover>
                                              <StyledTableCell>
                                                {row.Sub_Business_Name}
                                              </StyledTableCell>
                                              <StyledTableCell align="right">
                                                {row.Total_Bills}
                                              </StyledTableCell>
                                              <StyledTableCell align="right">
                                                {row.Total_Sales?.toFixed(2)}
                                              </StyledTableCell>
                                              <StyledTableCell align="right">
                                                {row.Total_GST?.toFixed(2)}
                                              </StyledTableCell>
                                              <StyledTableCell align="right">
                                                {row.Total_Cess?.toFixed(2)}
                                              </StyledTableCell>
                                              <StyledTableCell align="right">
                                                {row.Total_Disc?.toFixed(2)}
                                              </StyledTableCell>
                                            </StyledTableRow>
                                          )
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </CardContent>
                              )}
                              {showFullDetails && pagecount > 1 && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    px: 3,
                                    py: 2,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.50',
                                    flexWrap: 'wrap',
                                    gap: 2
                                  }}
                                >
                                  <Pagination
                                    count={pagecount}
                                    page={page}
                                    color="secondary"
                                    size="medium"
                                    boundaryCount={1}
                                    siblingCount={1}
                                    showFirstButton
                                    showLastButton
                                    onChange={handlePageChange}
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        borderRadius: '6px',
                                        '&:hover': {
                                          backgroundColor: 'secondary.light',
                                          color: 'white'
                                        }
                                      },
                                      '& .Mui-selected': {
                                        backgroundColor: 'secondary.main',
                                        color: 'white',
                                        fontWeight: 600,
                                        '&:hover': {
                                          backgroundColor: 'secondary.dark'
                                        }
                                      }
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      backgroundColor: 'white',
                                      px: 2.5,
                                      py: 1.25,
                                      borderRadius: '8px',
                                      border: '1px solid',
                                      borderColor: 'grey.300',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                      sx={{
                                        fontWeight: 600,
                                        minWidth: 'fit-content',
                                        fontSize: '0.875rem',
                                        letterSpacing: '0.01em'
                                      }}
                                    >
                                      Jump to
                                    </Typography>
                                    <TextField
                                      size="small"
                                      placeholder="Page"
                                      value={goToPageInput}
                                      onChange={handleGoToPageChange}
                                      onKeyPress={handleGoToPageKeyPress}
                                      sx={{
                                        width: 75,
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: 'grey.50',
                                          borderRadius: '6px',
                                          transition: 'all 0.2s ease-in-out',
                                          '& fieldset': {
                                            borderColor: 'grey.300',
                                            borderWidth: '1px'
                                          },
                                          '&:hover fieldset': {
                                            borderColor: 'secondary.main',
                                            borderWidth: '1.5px'
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: 'secondary.main',
                                            borderWidth: '2px',
                                            boxShadow:
                                              '0 0 0 3px rgba(156, 39, 176, 0.1)'
                                          }
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          paddingTop: '7px',
                                          paddingBottom: '7px',
                                          height: '20px',
                                          lineHeight: '20px',
                                          fontSize: '0.875rem'
                                        }
                                      }}
                                      inputProps={{
                                        min: 1,
                                        max: pagecount,
                                        style: {
                                          textAlign: 'center',
                                          fontWeight: 600
                                        }
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        minWidth: '40px'
                                      }}
                                    >
                                      of {pagecount}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="secondary"
                                      onClick={handleGoToPageSubmit}
                                      disabled={
                                        !goToPageInput ||
                                        parseInt(goToPageInput) < 1 ||
                                        parseInt(goToPageInput) > pagecount
                                      }
                                      sx={{
                                        minWidth: 65,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        py: 0.75,
                                        px: 2,
                                        height: '34px',
                                        lineHeight: 1,
                                        fontSize: '0.875rem',
                                        borderRadius: '6px',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                          transform: 'translateY(-1px)',
                                          boxShadow:
                                            '0 4px 8px rgba(0,0,0,0.15)'
                                        },
                                        '&:active': {
                                          transform: 'translateY(0px)',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        },
                                        '&.Mui-disabled': {
                                          backgroundColor: 'grey.300',
                                          color: 'grey.500'
                                        }
                                      }}
                                    >
                                      Go
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </MainCard>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                )}
              {!outletSummaryLoading &&
                !outletSummaryError &&
                !reportVisibility && <ShowNoRecordsFound />}
            </div>
          )}
        </div>
      }

      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>
    </>
  )
}

export default FranchisewiseSummaryReport
